import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to persist selection state in sessionStorage.
 * Selections survive navigation within the same tab but clear when tab closes.
 * 
 * @param storageKey - Unique key for this selection context (e.g., 'legislation-bill-selection')
 */
export const usePersistedSelection = (storageKey: string) => {
  // Initialize from sessionStorage
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Sync to sessionStorage on change
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify([...selectedIds]));
  }, [selectedIds, storageKey]);

  const handleSelect = useCallback((id: string, selected: boolean) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (selected) newSet.add(id);
      else newSet.delete(id);
      return newSet;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  return { selectedIds, handleSelect, clearSelection, selectAll, setSelectedIds };
};
